import { useCallback, useEffect, useRef, useState } from 'react';

export const TAILSCALE_REFRESH_INTERVAL_V91 = 15000;
export const TAILSCALE_REQUEST_TIMEOUT_V91 = 5000;

export default function useTailscaleServersV91(enabled, requestServers) {
  const [state, setState] = useState({
    servers: [],
    loading: false,
    failed: false,
  });
  const refreshRef = useRef(null);
  const refresh = useCallback(() => refreshRef.current?.(), []);

  useEffect(() => {
    if (!enabled) {
      setState({ servers: [], loading: false, failed: false });
      return undefined;
    }
    let active = true;
    let inFlight = null;
    const load = () => {
      if (!active || document.visibilityState === 'hidden') return undefined;
      if (inFlight) return inFlight;
      setState((current) => ({ ...current, loading: true }));
      // Bound the request and ignore late results when this view is closed.
      inFlight = Promise.resolve()
        .then(() =>
          requestServers({
            timeout: TAILSCALE_REQUEST_TIMEOUT_V91,
            params: { _ts: Date.now() },
          })
        )
        .then((response) => {
          if (
            response?.data?.success !== true ||
            !Array.isArray(response.data.data)
          ) {
            throw new Error('Tailscale discovery failed');
          }
          if (active) {
            setState({
              servers: response.data.data,
              loading: false,
              failed: false,
            });
          }
        })
        .catch(() => {
          if (active) {
            setState((current) => ({
              ...current,
              loading: false,
              failed: true,
            }));
          }
        })
        .finally(() => {
          inFlight = null;
        });
      return inFlight;
    };
    refreshRef.current = load;
    load();
    const timer = window.setInterval(load, TAILSCALE_REFRESH_INTERVAL_V91);
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', load);
    return () => {
      active = false;
      refreshRef.current = null;
      window.clearInterval(timer);
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', load);
    };
  }, [enabled, requestServers]);

  return { ...state, refresh };
}
